import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { Tooltip, TooltipContext } from "@animech/ui";
import { useEffect, useMemo, useRef } from "react";
import * as Styled from "./node-overlay.style";
import { CanvasOverlay } from "./canvas-overlay";
import { EditButtons } from "ui/components/elements/edit-buttons/edit-buttons";
import { NodeComponentType } from "store/types";
import { useNodeActions } from "hooks/node-actions";
import { GripPositionOverlayContent } from "ui/components/panels/canvas/node-components/grip-position/grip-position-overlay-content";
import { CentralizedPumpOverlayContent } from "ui/components/panels/canvas/node-components/centralized-vacuum-pump/centralized-pump-overlay-content";
import { PaddingLessTooltipClassName, ScrollableTooltipClassName, ZIndex, } from "ui/app/global.style";
/**
 * Provides a separate TooltipContext with a custom root element portalled to
 * document.body. This allows TooltipPortal components inside the overlay to
 * render at a different z-index than the app-level TooltipRoot.
 *
 * We avoid using <TooltipProvider> + createPortal(<TooltipRoot/>, body) because
 * that combination causes the app-level tooltip root to become detached,
 * breaking dropdowns in the sidebar drawers.
 */
var OverlayTooltipProvider = function (_a) {
    var children = _a.children, zIndex = _a.zIndex;
    var rootRef = useRef(null);
    if (!rootRef.current) {
        var div = document.createElement("div");
        div.style.cssText = "position:fixed;top:0;right:0;bottom:0;left:0;height:100%;width:100%;pointer-events:none;z-index:".concat(zIndex);
        rootRef.current = div;
    }
    useEffect(function () {
        var el = rootRef.current;
        if (!el)
            return;
        document.body.appendChild(el);
        return function () {
            el.remove();
        };
    }, []);
    var context = useMemo(function () {
        var _a;
        return ({
            root: (_a = rootRef.current) !== null && _a !== void 0 ? _a : undefined,
            setRoot: function () { },
        });
    }, []);
    return (_jsx(TooltipContext.Provider, { value: context, children: children }));
};
export var NodeOverlay = function (_a) {
    var _b = _a.isZoomedIn, isZoomedIn = _b === void 0 ? false : _b, nodeBoundingBoxHeight = _a.nodeBoundingBoxHeight, nodeTopY = _a.nodeTopY, nodeCenterX = _a.nodeCenterX, nodeCenterY = _a.nodeCenterY, node = _a.node;
    var actions = useNodeActions(node);
    var isGripPosition = node.type === NodeComponentType.GRIP_POSITION;
    var isCentralizedPump = node.type === NodeComponentType.CENTRALIZED_VACUUM_PUMP;
    var buttons = isZoomedIn ? (_jsx(Styled.NodeOverlay, { nodeBoundingBoxHeight: nodeBoundingBoxHeight, children: _jsx(EditButtons, { actions: actions, hasBackground: true }) })) : (_jsxs(Styled.NodeOverlay, { nodeBoundingBoxHeight: nodeBoundingBoxHeight, nodeBoundingBoxMargin: isGripPosition || isCentralizedPump ? 8 : undefined, children: [isGripPosition || isCentralizedPump ? (_jsxs(_Fragment, { children: [_jsx(Styled.BBox, {}), _jsx(OverlayTooltipProvider, { zIndex: ZIndex.OVERLAY_TOOLTIP, children: _jsx(Tooltip, { className: "".concat(PaddingLessTooltipClassName, " ").concat(ScrollableTooltipClassName), color: "neutral", hideArrow: true, open: true, children: isGripPosition ? (_jsx(GripPositionOverlayContent, { actions: actions, node: node })) : (_jsx(CentralizedPumpOverlayContent, { actions: actions, node: node })) }) })] })) : (_jsx(Styled.BBox, {})), !isGripPosition && !isCentralizedPump && (_jsx(EditButtons, { actions: actions }))] }));
    return (_jsx(CanvasOverlay, { x: nodeCenterX, y: nodeTopY, children: buttons }, isGripPosition || isCentralizedPump
        ? "".concat(nodeCenterX).concat(nodeCenterY)
        : undefined));
};
